/**
 * useMDMValueOrder (ORCH-383)
 *
 * Purpose: State for MDM Manager's Reorder mode and Reset order action on one list.
 * Logic: Reads where the list's order comes from and whether the caller may change it
 *        (GET …/value-order). Entering Reorder mode loads the whole list (inactive values
 *        too), because a saved order names every value. Saving or resetting invalidates
 *        the list's cached values so every open picker refetches the new order.
 * Dependencies: mdmService, React Query, mdmCacheKeys
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mdmService } from '../services/mdmService';
import { mdmCacheKeys } from '../services/mdmCacheService';
import { MasterDataValue, ValueOrderSourceResponse } from '../types/mdm';

export const valueOrderQueryKey = (fielddefId: string, tenantId: string) =>
  ['mdm', 'value-order', fielddefId, tenantId] as const;

export interface UseMDMValueOrderResult {
  source: ValueOrderSourceResponse['source'] | undefined;
  canReorder: boolean;
  /** The rows being reordered, or null outside Reorder mode */
  reorderRows: MasterDataValue[] | null;
  busy: boolean;
  error: string | null;
  startReorder: () => Promise<void>;
  setReorderRows: (rows: MasterDataValue[]) => void;
  cancelReorder: () => void;
  saveReorder: () => Promise<void>;
  resetOrder: () => Promise<void>;
}

export const useMDMValueOrder = (
  fielddefId: string | undefined,
  tenantId: string,
  enabled: boolean,
  onChanged: () => void
): UseMDMValueOrderResult => {
  const queryClient = useQueryClient();
  const [reorderRows, setRows] = useState<MasterDataValue[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orderQuery = useQuery({
    queryKey: valueOrderQueryKey(fielddefId ?? '', tenantId),
    queryFn: () => mdmService.getValueOrderSource(fielddefId as string),
    enabled: enabled && !!fielddefId,
  });

  const afterChange = useCallback(async () => {
    if (!fielddefId) return;
    setRows(null);
    await queryClient.invalidateQueries({ queryKey: ['mdm', 'values', fielddefId] });
    await queryClient.invalidateQueries({ queryKey: mdmCacheKeys.tenant(tenantId) });
    await queryClient.invalidateQueries({ queryKey: valueOrderQueryKey(fielddefId, tenantId) });
    onChanged();
  }, [fielddefId, tenantId, queryClient, onChanged]);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      setError(err?.response?.data?.detail?.message || err?.message || 'Could not save the order');
    } finally {
      setBusy(false);
    }
  }, []);

  const startReorder = useCallback(
    () =>
      run(async () => {
        const rows = await mdmService.getValues({
          fielddef_id: fielddefId,
          tenant_id: tenantId,
          include_inactive: true,
        });
        setRows(rows);
      }),
    [run, fielddefId, tenantId]
  );

  const saveReorder = useCallback(
    () =>
      run(async () => {
        if (!fielddefId || !reorderRows) return;
        await mdmService.setValueOrder(
          fielddefId,
          reorderRows.map((row) => row.global_value_id || row.id)
        );
        await afterChange();
      }),
    [run, fielddefId, reorderRows, afterChange]
  );

  const resetOrder = useCallback(
    () =>
      run(async () => {
        if (!fielddefId) return;
        await mdmService.resetValueOrder(fielddefId);
        await afterChange();
      }),
    [run, fielddefId, afterChange]
  );

  return {
    source: orderQuery.data?.source,
    canReorder: enabled && !!orderQuery.data?.can_reorder,
    reorderRows,
    busy,
    error,
    startReorder,
    setReorderRows: setRows,
    cancelReorder: () => {
      setRows(null);
      setError(null);
    },
    saveReorder,
    resetOrder,
  };
};

export default useMDMValueOrder;
